import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { useOrganization } from './OrganizationContext.jsx';
import { useSocket } from './SocketContext.jsx';
import conversationService from '../services/conversationService.js';
import fileService from '../services/fileService.js';

const ChatContext = createContext(null);
const CONVERSATION_LIST_TTL_MS = 60 * 1000;
const MESSAGE_THREAD_TTL_MS = 5 * 60 * 1000;

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return ctx;
};

/** Normalize any id-ish value (string, ObjectId-like, populated doc) to string. */
function toId(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value._id) return value._id.toString();
  return value.toString();
}

function toUnreadCount(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function senderId(message) {
  return toId(message?.sender);
}

/** Sort conversations by most-recent activity first. */
function sortConversations(list) {
  const deduped = Array.from(
    list.reduce((map, item) => {
      const id = toId(item?._id);
      if (!id) return map;
      map.set(id, { ...(map.get(id) || {}), ...item });
      return map;
    }, new Map()).values()
  );

  return deduped.sort((a, b) => {
    const ta = new Date(a.lastMessageAt || a.createdAt || 0).getTime();
    const tb = new Date(b.lastMessageAt || b.createdAt || 0).getTime();
    return tb - ta;
  });
}

const EMPTY_THREAD = {
  items: [],
  nextCursor: null,
  loaded: false,
  loadingOlder: false,
  hasMore: false,
  loadedAt: null,
};

export const ChatProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const { socket, isConnected } = useSocket();
  const location = useLocation();

  // Active workspace context derived from the current route.
  const workspaceType = location.pathname.startsWith('/org') ? 'organization' : 'personal';
  const organizationId = workspaceType === 'organization' ? toId(currentOrganization?._id) : null;
  const ctx = { workspaceType, organizationId };
  const workspaceKey = `${workspaceType}:${organizationId || 'personal'}`;
  const isMessagesRoute = location.pathname === '/messages' || location.pathname === '/org/messages';

  const [conversations, setConversations] = useState([]);
  const [conversationsCursor, setConversationsCursor] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [viewByConversation, setViewByConversation] = useState({});
  const [typingByConversation, setTypingByConversation] = useState({});
  const [presence, setPresence] = useState({});
  const [unread, setUnread] = useState({}); // { [conversationId]: count }
  const [unreadMarkers, setUnreadMarkers] = useState({}); // one-time in-thread separators

  const meId = toId(currentUser?._id);

  // Refs for use inside socket handlers (avoid stale closures).
  const activeIdRef = useRef(null);
  const ctxRef = useRef(ctx);
  const workspaceKeyRef = useRef(workspaceKey);
  const workspaceCacheRef = useRef(new Map());
  const stateSnapshotRef = useRef(null);
  const lastConversationLoadAtRef = useRef(0);
  const previousActiveConversationRef = useRef(null);
  const seenIncomingMessageIdsRef = useRef(new Set());
  const readReceiptStateRef = useRef(new Map());
  const conversationsRef = useRef(conversations);
  const unreadRef = useRef(unread);
  const isMessagesRouteRef = useRef(isMessagesRoute);
  activeIdRef.current = activeConversationId;
  ctxRef.current = ctx;
  conversationsRef.current = conversations;
  unreadRef.current = unread;
  isMessagesRouteRef.current = isMessagesRoute;

  useEffect(() => {
    stateSnapshotRef.current = {
      conversations,
      conversationsCursor,
      messagesByConversation,
      viewByConversation,
      activeConversationId,
      unread,
      unreadMarkers,
      loadedAt: lastConversationLoadAtRef.current,
    };
  }, [
    conversations,
    conversationsCursor,
    messagesByConversation,
    viewByConversation,
    activeConversationId,
    unread,
    unreadMarkers,
  ]);

  useEffect(() => {
    const previous = previousActiveConversationRef.current;
    if (previous && previous !== activeConversationId) {
      setUnreadMarkers((prev) => {
        if (!prev[previous]) return prev;
        const next = { ...prev };
        delete next[previous];
        return next;
      });
    }
    previousActiveConversationRef.current = activeConversationId;
  }, [activeConversationId]);

  // ---- Conversation list helpers ---------------------------------------

  const upsertConversation = useCallback((partial) => {
    const cid = toId(partial._id);
    if (!cid) return;
    setConversations((prev) => {
      const idx = prev.findIndex((c) => toId(c._id) === cid);
      let next;
      if (idx >= 0) {
        next = [...prev];
        next[idx] = { ...next[idx], ...partial };
      } else {
        next = [...prev, partial];
      }
      return sortConversations(next);
    });
  }, []);

  const bumpConversationPreview = useCallback((conversationId, message) => {
    const cid = toId(conversationId);
    setConversations((prev) => {
      const idx = prev.findIndex((c) => toId(c._id) === cid);
      if (idx < 0) return prev;
      const next = [...prev];
      const preview =
        message.type === 'text'
          ? (message.content || '')
          : message.type === 'image'
            ? '🖼 Photo'
            : message.type === 'file'
              ? '📎 File'
              : message.type === 'share'
                ? '🔗 Shared item'
                : '';
      next[idx] = {
        ...next[idx],
        lastMessage: {
          messageId: message._id,
          preview,
          sender: toId(message.sender),
          type: message.type,
          sentAt: message.createdAt,
        },
        lastMessageAt: message.createdAt || new Date().toISOString(),
      };
      return sortConversations(next);
    });
  }, []);

  const loadConversations = useCallback(
    async ({ reset = true, force = false } = {}) => {
      if (!meId) return;
      if (
        reset &&
        !force &&
        conversations.length > 0 &&
        Date.now() - lastConversationLoadAtRef.current < CONVERSATION_LIST_TTL_MS
      ) {
        return;
      }
      setLoadingConversations(true);
      try {
        const { items, nextCursor } = await conversationService.listConversations(
          ctxRef.current,
          reset ? {} : { cursor: conversationsCursor }
        );
        setConversations((prev) =>
          reset ? sortConversations(items) : sortConversations([...prev, ...items])
        );
        setConversationsCursor(nextCursor);
        if (reset) lastConversationLoadAtRef.current = Date.now();
        // Seed unread from server truth on (re)load.
        if (reset) {
          const seed = {};
          for (const c of items) {
            const count = toUnreadCount(c.unreadCount) || (c.hasUnread ? 1 : 0);
            if (count > 0) seed[toId(c._id)] = count;
          }
          setUnread(seed);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] loadConversations failed:', err?.message || err);
      } finally {
        setLoadingConversations(false);
      }
    },
    [meId, conversations.length, conversationsCursor]
  );

  // Reset + reload whenever the active workspace changes (or user logs in).
  useEffect(() => {
    const previousWorkspaceKey = workspaceKeyRef.current;
    const snapshot = stateSnapshotRef.current;
    if (previousWorkspaceKey && previousWorkspaceKey !== workspaceKey && snapshot) {
      workspaceCacheRef.current.set(previousWorkspaceKey, {
        ...snapshot,
        cachedAt: Date.now(),
      });
    }
    workspaceKeyRef.current = workspaceKey;

    if (!meId) {
      setConversations([]);
      setMessagesByConversation({});
      setViewByConversation({});
      setActiveConversationId(null);
      setUnread({});
      setUnreadMarkers({});
      return;
    }

    const cached = workspaceCacheRef.current.get(workspaceKey);
    if (cached && Date.now() - cached.cachedAt < MESSAGE_THREAD_TTL_MS) {
      setConversations(cached.conversations || []);
      setConversationsCursor(cached.conversationsCursor || null);
      setMessagesByConversation(cached.messagesByConversation || {});
      setViewByConversation(cached.viewByConversation || {});
      setActiveConversationId(cached.activeConversationId || null);
      setUnread(cached.unread || {});
      setUnreadMarkers(cached.unreadMarkers || {});
      lastConversationLoadAtRef.current = cached.loadedAt || 0;
      loadConversations({ reset: true });
      return;
    }

    setConversations([]);
    setConversationsCursor(null);
    setMessagesByConversation({});
    setViewByConversation({});
    setActiveConversationId(null);
    setUnread({});
    setUnreadMarkers({});
    lastConversationLoadAtRef.current = 0;
    loadConversations({ reset: true, force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId, workspaceKey]);

  // ---- Messages --------------------------------------------------------

  const loadMessages = useCallback(
    async (conversationId, { older = false, force = false } = {}) => {
      const cid = toId(conversationId);
      if (!cid) return;
      const thread = messagesByConversation[cid] || EMPTY_THREAD;
      if (older && (!thread.hasMore || thread.loadingOlder)) return;
      if (
        !older &&
        !force &&
        thread.loaded &&
        Date.now() - (thread.loadedAt || 0) < MESSAGE_THREAD_TTL_MS
      ) {
        return;
      }

      setMessagesByConversation((prev) => ({
        ...prev,
        [cid]: { ...(prev[cid] || EMPTY_THREAD), loadingOlder: older },
      }));

      try {
        const { items, nextCursor } = await conversationService.getMessages(
          ctxRef.current,
          cid,
          older ? { cursor: thread.nextCursor } : {}
        );
        // Service returns newest-first; store ascending (oldest-first).
        const ascending = [...items].reverse();
        setMessagesByConversation((prev) => {
          const existing = prev[cid] || EMPTY_THREAD;
          const merged = older
            ? dedupeById([...ascending, ...existing.items])
            : dedupeById([...ascending]);
          return {
            ...prev,
            [cid]: {
              items: merged,
              nextCursor,
              hasMore: Boolean(nextCursor),
              loaded: true,
              loadingOlder: false,
              loadedAt: Date.now(),
            },
          };
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] loadMessages failed:', err?.message || err);
        setMessagesByConversation((prev) => ({
          ...prev,
          [cid]: { ...(prev[cid] || EMPTY_THREAD), loadingOlder: false },
        }));
      }
    },
    [messagesByConversation]
  );

  const loadConversationView = useCallback(async (conversationId) => {
    const cid = toId(conversationId);
    if (!cid) return;
    try {
      const view = await conversationService.getConversation(ctxRef.current, cid);
      setViewByConversation((prev) => ({ ...prev, [cid]: view }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ChatContext] loadConversationView failed:', err?.message || err);
    }
  }, []);

  const markRead = useCallback(
    async (conversationId, { force = false } = {}) => {
      const cid = toId(conversationId);
      if (!cid) return;

      const conversation = conversationsRef.current.find((c) => toId(c._id) === cid);
      const hasLocalUnread =
        toUnreadCount(unreadRef.current[cid]) > 0 ||
        toUnreadCount(conversation?.unreadCount) > 0 ||
        Boolean(conversation?.hasUnread);

      if (!force && !hasLocalUnread) return;

      setUnread((prev) => {
        if (!prev[cid]) return prev;
        const next = { ...prev };
        delete next[cid];
        return next;
      });
      upsertConversation({ _id: cid, hasUnread: false, unreadCount: 0, lastReadAt: new Date().toISOString() });

      const receiptKey = `${workspaceKeyRef.current}:${cid}`;
      const now = Date.now();
      const previous = readReceiptStateRef.current.get(receiptKey);
      if (previous?.inFlight || now - (previous?.lastSentAt || 0) < 1500) return;

      readReceiptStateRef.current.set(receiptKey, { inFlight: true, lastSentAt: now });
      try {
        await conversationService.markRead(ctxRef.current, cid);
        if (socket && isConnected) socket.emit('chat:read', { conversationId: cid });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] markRead failed:', err?.message || err);
      } finally {
        const latest = readReceiptStateRef.current.get(receiptKey) || {};
        readReceiptStateRef.current.set(receiptKey, { ...latest, inFlight: false });
      }
    },
    [socket, isConnected, upsertConversation]
  );

  const applyPresenceSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return;
    setPresence((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [userId, online] of Object.entries(snapshot)) {
        const id = toId(userId);
        if (!id) continue;
        if (next[id] !== !!online) {
          next[id] = !!online;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const openConversation = useCallback(
    async (conversationId) => {
      const cid = toId(conversationId);
      if (!cid) return;
      const unreadCount =
        toUnreadCount(unread[cid]) ||
        toUnreadCount(conversations.find((c) => toId(c._id) === cid)?.unreadCount);
      setActiveConversationId(cid);
      if (socket && isConnected) {
        socket.emit('chat:join', { conversationId: cid }, (ack) => {
          if (ack?.success && ack.presence) applyPresenceSnapshot(ack.presence);
        });
      }
      const thread = messagesByConversation[cid];
      if (!thread || !thread.loaded || (isMessagesRoute && unreadCount > 0)) {
        await loadMessages(cid, { force: isMessagesRoute && unreadCount > 0 });
      }
      if (!viewByConversation[cid]) {
        loadConversationView(cid);
      }
      setUnreadMarkers((prev) => {
        if (unreadCount <= 0) return prev;
        return { ...prev, [cid]: { count: unreadCount, openedAt: Date.now() } };
      });
      if (isMessagesRoute && unreadCount > 0) {
        markRead(cid);
      }
    },
    [
      socket,
      isConnected,
      unread,
      conversations,
      messagesByConversation,
      viewByConversation,
      loadMessages,
      loadConversationView,
      markRead,
      applyPresenceSnapshot,
      isMessagesRoute,
    ]
  );

  useEffect(() => {
    if (!isMessagesRoute || !activeConversationId) return;
    const cid = toId(activeConversationId);
    const unreadCount =
      toUnreadCount(unread[cid]) ||
      toUnreadCount(conversations.find((c) => toId(c._id) === cid)?.unreadCount);
    if (unreadCount <= 0) return;

    setUnreadMarkers((prev) => ({
      ...prev,
      [cid]: { count: unreadCount, openedAt: Date.now() },
    }));
    loadMessages(cid, { force: true });
    markRead(cid);
  }, [isMessagesRoute, activeConversationId, unread, conversations, loadMessages, markRead]);

  const openDirect = useCallback(
    async (targetUserId) => {
      try {
        const conversation = await conversationService.openDirect(ctxRef.current, targetUserId);
        upsertConversation(conversation);
        await openConversation(conversation._id);
        return conversation;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] openDirect failed:', err?.message || err);
        throw err;
      }
    },
    [upsertConversation, openConversation]
  );

  // Get-or-create the shared conversation for a project. By default this only
  // resolves the conversation (used by the in-project chat widget); pass
  // { activate: true } to also open it in the Messages hub.
  const openProject = useCallback(
    async (projectId, { activate = false } = {}) => {
      try {
        const conversation = await conversationService.openProject(ctxRef.current, projectId);
        upsertConversation(conversation);
        if (activate) {
          await openConversation(conversation._id);
        }
        return conversation;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] openProject failed:', err?.message || err);
        throw err;
      }
    },
    [upsertConversation, openConversation]
  );

  // Create a custom group conversation, then open it (Phase 5.3).
  const createGroup = useCallback(
    async ({ name, memberIds }) => {
      const conversation = await conversationService.createGroup(ctxRef.current, {
        name,
        memberIds,
      });
      upsertConversation(conversation);
      await openConversation(conversation._id);
      return conversation;
    },
    [upsertConversation, openConversation]
  );

  // Group management mutations. Each refreshes the conversation list so the
  // sidebar reflects new names / membership immediately.
  const renameGroup = useCallback(
    async (conversationId, name) => {
      const conversation = await conversationService.renameGroup(
        ctxRef.current,
        toId(conversationId),
        name
      );
      upsertConversation(conversation);
      return conversation;
    },
    [upsertConversation]
  );

  const updateConversationProfile = useCallback(
    async (conversationId, profile) => {
      const conversation = await conversationService.updateProfile(
        ctxRef.current,
        toId(conversationId),
        profile
      );
      upsertConversation(conversation);
      setViewByConversation((prev) => ({ ...prev, [toId(conversation._id)]: conversation }));
      return conversation;
    },
    [upsertConversation]
  );

  const addGroupMembers = useCallback(
    async (conversationId, memberIds) =>
      conversationService.addGroupMembers(ctxRef.current, toId(conversationId), memberIds),
    []
  );

  const removeGroupMember = useCallback(
    async (conversationId, memberId) =>
      conversationService.removeGroupMember(ctxRef.current, toId(conversationId), memberId),
    []
  );

  const updateGroupMemberRole = useCallback(
    async (conversationId, memberId, role) =>
      conversationService.updateGroupMemberRole(
        ctxRef.current,
        toId(conversationId),
        memberId,
        role
      ),
    []
  );

  const leaveGroup = useCallback(
    async (conversationId) => {
      const result = await conversationService.leaveGroup(ctxRef.current, toId(conversationId));
      loadConversations({ reset: true });
      return result;
    },
    [loadConversations]
  );

  // Per-conversation preference flags (mute / pin / archive / star). Private
  // to the caller. Optimistically reflect the toggle in the sidebar.
  const updateConversationFlags = useCallback(
    async (conversationId, flags) => {
      const cid = toId(conversationId);
      upsertConversation({ _id: cid, ...flags });
      try {
        const conversation = await conversationService.updateFlags(ctxRef.current, cid, flags);
        upsertConversation(conversation);
        return conversation;
      } catch (err) {
        loadConversations({ reset: true });
        throw err;
      }
    },
    [upsertConversation, loadConversations]
  );

  // ---- Sending / editing ----------------------------------------------

  const appendMessage = useCallback((conversationId, message) => {
    const cid = toId(conversationId);
    setMessagesByConversation((prev) => {
      const existing = prev[cid];
      if (!existing || !existing.loaded) return prev; // thread not open; list preview handles it
      return {
        ...prev,
        [cid]: { ...existing, items: dedupeById([...existing.items, message]) },
      };
    });
  }, []);

  const rememberIncomingMessage = useCallback((messageId) => {
    const id = toId(messageId);
    if (!id) return false;
    const seen = seenIncomingMessageIdsRef.current;
    const alreadySeen = seen.has(id);
    seen.add(id);
    if (seen.size > 500) {
      const oldest = seen.values().next().value;
      seen.delete(oldest);
    }
    return alreadySeen;
  }, []);

  const uploadAttachment = useCallback(
    async (conversationId, file, onProgress) => {
      const cid = toId(conversationId);
      if (!cid) throw new Error('Conversation is required to upload an attachment');
      const context = { type: 'chat', conversationId: cid };
      const confirmed =
        workspaceType === 'organization'
          ? await fileService.uploadOrganizationFile(organizationId, file, { context }, onProgress)
          : await fileService.uploadPersonalFile(file, { context }, onProgress);
      return confirmed;
    },
    [workspaceType, organizationId]
  );

  const sendMessage = useCallback(
    async (conversationId, payload) => {
      const cid = toId(conversationId);
      if (!cid) return;
      const nonce = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimistic = {
        _id: nonce,
        clientNonce: nonce,
        conversation: cid,
        sender: meId,
        type: payload.type || 'text',
        content: payload.content || '',
        attachments: payload.attachments || [],
        sharedResource: payload.sharedResource || null,
        replyTo: payload.replyTo || null,
        mentions: payload.mentions || [],
        reactions: [],
        deleted: false,
        pending: true,
        createdAt: new Date().toISOString(),
      };
      appendMessage(cid, optimistic);
      bumpConversationPreview(cid, optimistic);

      try {
        const saved = await conversationService.sendMessage(ctxRef.current, cid, payload);
        // Replace the optimistic placeholder with the durable message.
        setMessagesByConversation((prev) => {
          const existing = prev[cid];
          if (!existing) return prev;
          const items = existing.items.map((m) => (m._id === nonce ? saved : m));
          return { ...prev, [cid]: { ...existing, items: dedupeById(items) } };
        });
        bumpConversationPreview(cid, saved);
        return saved;
      } catch (err) {
        // Mark the optimistic message as failed.
        setMessagesByConversation((prev) => {
          const existing = prev[cid];
          if (!existing) return prev;
          const items = existing.items.map((m) =>
            m._id === nonce ? { ...m, pending: false, failed: true } : m
          );
          return { ...prev, [cid]: { ...existing, items } };
        });
        // eslint-disable-next-line no-console
        console.error('[ChatContext] sendMessage failed:', err?.message || err);
        throw err;
      }
    },
    [meId, appendMessage, bumpConversationPreview]
  );

  const shareResource = useCallback(
    async (conversationId, sharedResource, caption = '') =>
      sendMessage(conversationId, {
        type: 'share',
        content: caption || '',
        sharedResource,
      }),
    [sendMessage]
  );

  const editMessage = useCallback(async (conversationId, messageId, content) => {
    const cid = toId(conversationId);
    try {
      const updated = await conversationService.editMessage(ctxRef.current, cid, messageId, content);
      setMessagesByConversation((prev) => {
        const existing = prev[cid];
        if (!existing) return prev;
        const items = existing.items.map((m) => (toId(m._id) === toId(messageId) ? updated : m));
        return { ...prev, [cid]: { ...existing, items } };
      });
      return updated;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ChatContext] editMessage failed:', err?.message || err);
      throw err;
    }
  }, []);

  const deleteMessage = useCallback(async (conversationId, messageId) => {
    const cid = toId(conversationId);
    try {
      await conversationService.deleteMessage(ctxRef.current, cid, messageId);
      setMessagesByConversation((prev) => {
        const existing = prev[cid];
        if (!existing) return prev;
        const items = existing.items.map((m) =>
          toId(m._id) === toId(messageId)
            ? { ...m, deleted: true, content: '', attachments: [], sharedResource: null }
            : m
        );
        return { ...prev, [cid]: { ...existing, items } };
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ChatContext] deleteMessage failed:', err?.message || err);
      throw err;
    }
  }, []);

  const applyReaction = useCallback((conversationId, { messageId, emoji, userId, action }) => {
    const cid = toId(conversationId);
    setMessagesByConversation((prev) => {
      const existing = prev[cid];
      if (!existing) return prev;
      const items = existing.items.map((m) => {
        if (toId(m._id) !== toId(messageId)) return m;
        const reactions = Array.isArray(m.reactions) ? [...m.reactions] : [];
        const i = reactions.findIndex(
          (r) => toId(r.user) === toId(userId) && r.emoji === emoji
        );
        if (action === 'add' && i < 0) reactions.push({ user: toId(userId), emoji });
        if (action === 'remove' && i >= 0) reactions.splice(i, 1);
        return { ...m, reactions };
      });
      return { ...prev, [cid]: { ...existing, items } };
    });
  }, []);

  const toggleReaction = useCallback(
    async (conversationId, messageId, emoji) => {
      const cid = toId(conversationId);
      try {
        const result = await conversationService.toggleReaction(ctxRef.current, cid, messageId, emoji);
        applyReaction(cid, result);
        return result;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ChatContext] toggleReaction failed:', err?.message || err);
        throw err;
      }
    },
    [applyReaction]
  );

  // ---- Typing ----------------------------------------------------------

  const startTyping = useCallback(
    (conversationId) => {
      if (socket && isConnected) socket.emit('chat:typing', { conversationId: toId(conversationId) });
    },
    [socket, isConnected]
  );

  const stopTyping = useCallback(
    (conversationId) => {
      if (socket && isConnected) socket.emit('chat:typing:stop', { conversationId: toId(conversationId) });
    },
    [socket, isConnected]
  );

  // ---- Socket subscriptions -------------------------------------------

  useEffect(() => {
    if (!socket) return undefined;

    const incrementUnread = (conversationId, delta = 1) => {
      const cid = toId(conversationId);
      if (!cid || delta <= 0) return;
      const nextCount =
        toUnreadCount(unread[cid] || conversations.find((c) => toId(c._id) === cid)?.unreadCount) +
        delta;
      setUnread((prev) => ({ ...prev, [cid]: toUnreadCount(prev[cid]) + delta }));
      upsertConversation({
        _id: cid,
        hasUnread: true,
        unreadCount: nextCount,
      });
    };

    const onMessageNew = ({ conversationId, message }) => {
      const cid = toId(conversationId);
      const alreadyCounted = rememberIncomingMessage(message?._id);
      appendMessage(cid, message);
      bumpConversationPreview(cid, message);
      const chatIsVisible = isMessagesRouteRef.current && activeIdRef.current === cid;
      if (!chatIsVisible && senderId(message) !== meId && !alreadyCounted) {
        incrementUnread(cid, 1);
      } else if (senderId(message) !== meId) {
        markRead(cid, { force: true });
      }
    };

    const onMessageUpdated = ({ conversationId, message }) => {
      const cid = toId(conversationId);
      setMessagesByConversation((prev) => {
        const existing = prev[cid];
        if (!existing) return prev;
        const items = existing.items.map((m) =>
          toId(m._id) === toId(message._id) ? message : m
        );
        return { ...prev, [cid]: { ...existing, items } };
      });
    };

    const onMessageDeleted = ({ conversationId, messageId }) => {
      const cid = toId(conversationId);
      setMessagesByConversation((prev) => {
        const existing = prev[cid];
        if (!existing) return prev;
        const items = existing.items.map((m) =>
          toId(m._id) === toId(messageId)
            ? { ...m, deleted: true, content: '', attachments: [], sharedResource: null }
            : m
        );
        return { ...prev, [cid]: { ...existing, items } };
      });
    };

    const onReaction = ({ conversationId, messageId, emoji, userId, action }) => {
      applyReaction(conversationId, { messageId, emoji, userId, action });
    };

    const onTyping = ({ conversationId, userId, userName, typing }) => {
      const cid = toId(conversationId);
      if (toId(userId) === meId) return;
      setTypingByConversation((prev) => {
        const forConvo = { ...(prev[cid] || {}) };
        if (typing) forConvo[toId(userId)] = { name: userName, at: Date.now() };
        else delete forConvo[toId(userId)];
        return { ...prev, [cid]: forConvo };
      });
    };

    const onPresence = ({ userId, online }) => {
      setPresence((prev) => ({ ...prev, [toId(userId)]: !!online }));
    };

    const onConversationNew = ({ conversationId, preview, messageId, unreadDelta } = {}) => {
      const cid = toId(conversationId);
      const alreadyCounted = rememberIncomingMessage(messageId);
      const chatIsVisible = isMessagesRouteRef.current && activeIdRef.current === cid;
      if (cid && !chatIsVisible && !alreadyCounted) {
        incrementUnread(cid, toUnreadCount(unreadDelta));
        if (preview) {
          upsertConversation({
            _id: cid,
            hasUnread: true,
            unreadCount:
              toUnreadCount(unread[cid] || conversations.find((c) => toId(c._id) === cid)?.unreadCount) +
              toUnreadCount(unreadDelta),
            lastMessage: { ...(conversations.find((c) => toId(c._id) === cid)?.lastMessage || {}), preview },
            lastMessageAt: new Date().toISOString(),
          });
        }
      } else if (cid && chatIsVisible) {
        markRead(cid, { force: true });
      }
      // A new conversation surfaced for us; refresh the head of the list.
      loadConversations({ reset: true, force: true });
    };

    socket.on('chat:message:new', onMessageNew);
    socket.on('chat:message:updated', onMessageUpdated);
    socket.on('chat:message:deleted', onMessageDeleted);
    socket.on('chat:message:reaction', onReaction);
    socket.on('chat:typing', onTyping);
    socket.on('chat:presence', onPresence);
    socket.on('chat:conversation:new', onConversationNew);

    return () => {
      socket.off('chat:message:new', onMessageNew);
      socket.off('chat:message:updated', onMessageUpdated);
      socket.off('chat:message:deleted', onMessageDeleted);
      socket.off('chat:message:reaction', onReaction);
      socket.off('chat:typing', onTyping);
      socket.off('chat:presence', onPresence);
      socket.off('chat:conversation:new', onConversationNew);
    };
  }, [
    socket,
    meId,
    unread,
    conversations,
    appendMessage,
    bumpConversationPreview,
    upsertConversation,
    applyReaction,
    loadConversations,
    markRead,
    rememberIncomingMessage,
  ]);

  useEffect(() => {
    if (!socket || !isConnected || conversations.length === 0) return undefined;
    const conversationIds = conversations.map((c) => toId(c._id)).filter(Boolean);
    let cancelled = false;
    socket.emit('chat:presence:list', { conversationIds }, (ack) => {
      if (!cancelled && ack?.success && ack.presence) {
        applyPresenceSnapshot(ack.presence);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [socket, isConnected, conversations, applyPresenceSnapshot]);

  // Refresh chat state after AI-executed messaging actions.
  useEffect(() => {
    const onAiActionExecuted = (event) => {
      const action = event?.detail?.action;
      if (typeof action !== 'string' || !action.startsWith('communication.')) return;

      loadConversations({ reset: true, force: true });

      const activeId = activeIdRef.current;
      if (activeId) {
        loadMessages(activeId, { force: true });
        loadConversationView(activeId);
        markRead(activeId);
      }
    };

    window.addEventListener('ai:action-executed', onAiActionExecuted);
    return () => window.removeEventListener('ai:action-executed', onAiActionExecuted);
  }, [loadConversations, loadMessages, loadConversationView, markRead]);

  useEffect(() => {
    const onOpenConversation = (event) => {
      const conversationId = event?.detail?.conversationId;
      if (!conversationId) return;
      openConversation(conversationId);
    };

    window.addEventListener('chat:open-conversation', onOpenConversation);
    return () => window.removeEventListener('chat:open-conversation', onOpenConversation);
  }, [openConversation]);

  // Expire stale typing indicators (safety net if a stop event is missed).
  useEffect(() => {
    const interval = setInterval(() => {
      setTypingByConversation((prev) => {
        let changed = false;
        const next = {};
        const now = Date.now();
        for (const [cid, users] of Object.entries(prev)) {
          const kept = {};
          for (const [uid, info] of Object.entries(users)) {
            if (now - info.at < 6000) kept[uid] = info;
            else changed = true;
          }
          next[cid] = kept;
        }
        return changed ? next : prev;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const totalUnread = Object.values(unread).reduce(
    (sum, count) => sum + toUnreadCount(count),
    0
  );

  const value = {
    // workspace context
    workspaceType,
    organizationId,
    meId,
    // list
    conversations,
    loadingConversations,
    conversationsCursor,
    loadConversations,
    // active thread
    activeConversationId,
    setActiveConversationId,
    openConversation,
    openDirect,
    openProject,
    // groups (Phase 5.3)
    createGroup,
    renameGroup,
    updateConversationProfile,
    addGroupMembers,
    removeGroupMember,
    updateGroupMemberRole,
    leaveGroup,
    updateConversationFlags,
    messagesByConversation,
    viewByConversation,
    loadMessages,
    // mutations
    sendMessage,
    uploadAttachment,
    shareResource,
    editMessage,
    deleteMessage,
    toggleReaction,
    markRead,
    // realtime ancillary
    typingByConversation,
    presence,
    unread,
    unreadMarkers,
    totalUnread,
    startTyping,
    stopTyping,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

/** Remove duplicate messages by _id, preserving order (last wins on conflict). */
function dedupeById(items) {
  const seen = new Map();
  for (const m of items) {
    seen.set(toId(m._id), m);
  }
  return Array.from(seen.values());
}

export default ChatContext;
